import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, CalendarClock, FileDown, AlertTriangle, CheckCircle2, Share2 } from "lucide-react";
import { DURATION_UNITS, planInfo, computeEnd } from "@/lib/plan";
import { exportFullReport, shareFullReport } from "@/lib/pdf";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso, days) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function ReportDrawer({ open, setOpen, patient, setPlan, onSave }) {
  const plan = patient.plan || { start: "", end: "", value: "", unit: "settimane" };
  const info = planInfo(patient);

  const update = (patch) => setPlan({ ...plan, ...patch });

  const applyDuration = (value, unit) => {
    const start = plan.start || todayISO();
    update({ start, value, unit, end: computeEnd(start, value, unit) });
  };

  const addProroga = (days) => {
    const base = plan.end || (info.hasPlan ? info.end.toISOString().slice(0, 10) : plan.start || todayISO());
    update({ end: addDaysISO(base, days) });
    toast.success(`Proroga di ${days} giorni applicata`);
  };

  const doExport = () => {
    if (!patient.cognome?.trim() && !patient.nome?.trim()) {
      toast.error("Inserisci il nome del paziente prima di esportare");
      return;
    }
    const ok = exportFullReport(patient);
    if (!ok) toast.error("Consenti i popup per esportare il PDF");
    else toast.success("Report pronto: usa 'Salva come PDF' nella finestra di stampa");
  };

  const doShare = async () => {
    if (!patient.cognome?.trim() && !patient.nome?.trim()) {
      toast.error("Inserisci il nome del paziente prima di condividere");
      return;
    }
    const res = await shareFullReport(patient);
    if (res === "downloaded") toast.success("File scaricato: puoi allegarlo e inviarlo");
    else if (res === "shared") toast.success("Report condiviso");
  };

  let statusEl = null;
  if (info.hasPlan) {
    if (info.expired) {
      statusEl = (
        <div className="flex items-center gap-2 rounded-lg border border-[hsl(0_72%_60%)]/40 bg-[hsl(0_72%_60%)]/10 px-3 py-2.5 text-sm font-semibold text-[hsl(0_72%_65%)]" data-testid="plan-status">
          <AlertTriangle className="h-4 w-4" /> Piano scaduto da {Math.abs(info.daysLeft)} giorni
        </div>
      );
    } else {
      statusEl = (
        <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2.5 text-sm font-semibold text-primary" data-testid="plan-status">
          <CheckCircle2 className="h-4 w-4" /> {info.daysLeft} giorni rimanenti (scadenza {info.end.toLocaleDateString("it-IT")})
        </div>
      );
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="report-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground">
            <FileText className="h-5 w-5 text-primary" /> Report Percorso Cliente
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-6 overflow-y-auto pb-6" style={{ maxHeight: "calc(100vh - 120px)" }}>
          {/* Plan validity range */}
          <section className="space-y-3">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold"><CalendarClock className="h-4 w-4 text-[hsl(var(--c-teal))]" /> Scadenza del Piano Nutrizionale</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="field-label mb-1">Dal</div>
                <input type="date" value={plan.start || ""} onChange={(e) => update({ start: e.target.value })} className="lab-input" data-testid="plan-start-input" />
              </div>
              <div>
                <div className="field-label mb-1">Al (scadenza)</div>
                <input type="date" value={plan.end || ""} min={plan.start || undefined} onChange={(e) => update({ end: e.target.value })} className="lab-input" data-testid="plan-end-input" />
              </div>
            </div>

            <div className="rounded-lg border border-white/5 bg-black/20 px-3 py-2.5">
              <div className="field-label mb-2">Durata rapida (calcola l'“Al” dall'“Dal”)</div>
              <div className="flex items-center gap-2">
                <input type="number" min={1} value={plan.value || ""} onChange={(e) => applyDuration(e.target.value, plan.unit || "settimane")} placeholder="es. 8" className="lab-input w-24 font-mono-num" data-testid="plan-value-input" />
                <Select value={plan.unit || "settimane"} onValueChange={(v) => applyDuration(plan.value, v)}>
                  <SelectTrigger className="lab-input h-auto flex-1" data-testid="plan-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATION_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border border-white/5 bg-black/20 px-3 py-2.5">
              <div className="field-label mb-2">Proroga (sposta la scadenza “Al”)</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => addProroga(7)} className="rounded-md border border-[hsl(var(--c-teal))]/40 bg-[hsl(var(--c-teal))]/10 px-2.5 py-1.5 text-xs font-semibold text-[hsl(var(--c-teal))] transition-colors hover:bg-[hsl(var(--c-teal))]/20" data-testid="proroga-1w-btn">+1 settimana</button>
                <button onClick={() => addProroga(14)} className="rounded-md border border-[hsl(var(--c-teal))]/40 bg-[hsl(var(--c-teal))]/10 px-2.5 py-1.5 text-xs font-semibold text-[hsl(var(--c-teal))] transition-colors hover:bg-[hsl(var(--c-teal))]/20" data-testid="proroga-2w-btn">+2 settimane</button>
                <button onClick={() => addProroga(30)} className="rounded-md border border-[hsl(var(--c-teal))]/40 bg-[hsl(var(--c-teal))]/10 px-2.5 py-1.5 text-xs font-semibold text-[hsl(var(--c-teal))] transition-colors hover:bg-[hsl(var(--c-teal))]/20" data-testid="proroga-1m-btn">+1 mese</button>
              </div>
            </div>

            {!plan.start && (
              <button onClick={() => update({ start: todayISO() })} className="text-xs font-medium text-primary hover:underline" data-testid="plan-today-btn">Imposta inizio a oggi</button>
            )}
            {statusEl}
            <p className="text-[11px] text-muted-foreground">Alla scadenza, il cliente comparirà nel contatore delle notifiche. Ricordati di salvare il paziente.</p>
            <button onClick={() => onSave && onSave()} className="w-full rounded-lg border border-white/10 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/5" data-testid="plan-save-btn">Salva piano</button>
          </section>

          {/* Export */}
          <section className="space-y-3 border-t border-white/5 pt-5">
            <h3 className="font-display text-sm font-bold">Esporta Resoconto Completo</h3>
            <p className="text-[11px] text-muted-foreground">Il PDF include: Sezione 1 (Metabolismo &amp; Macro), Sezione 3 (Piano Alimentare), Sezione 5 (Misurazioni), Sezione 7 (Foto).</p>
            <button onClick={doExport} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="export-report-btn">
              <FileDown className="h-4 w-4" /> Esporta Report PDF
            </button>
            <button onClick={doShare} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/20" data-testid="share-report-btn">
              <Share2 className="h-4 w-4" /> Condividi / Invia Report
            </button>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
